// ═══════════════════════════════════════════════════════════════════════════
// BILAN HYDRIQUE - Analyse rétrospective et prévisions
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/BilanHydrique.jsx
// Farm-Booking - Papy 73+ - Session 01/01/2026
// "L'eau juste, pas une goutte de perdue"
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  Droplets, CloudRain, Sun, Thermometer, AlertTriangle,
  TrendingUp, TrendingDown, Calendar, Activity, RefreshCw,
  CheckCircle, XCircle, Cloud, Wind
} from 'lucide-react';
import meteoService from '../../../services/meteoService';

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Carte météo du jour
// ═══════════════════════════════════════════════════════════════════════════

const MeteoJourCard = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-sky-100 to-blue-100 rounded-xl p-5 animate-pulse">
        <div className="h-6 bg-sky-200 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-sky-200 rounded"></div>
      </div>
    );
  }

  if (!data || !data.daily || !data.daily[0]) {
    return (
      <div className="bg-gray-100 rounded-xl p-5 text-center text-gray-500">
        Données météo indisponibles
      </div>
    );
  }

  const aujourdhui = data.daily[0];

  return (
    <div className="bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl p-5 text-white shadow-lg">
      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
        <Sun className="w-6 h-6" />
        Météo Aujourd'hui - {data.latitude?.toFixed(2)}°N, {data.longitude?.toFixed(2)}°E
      </h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
          <Thermometer className="w-6 h-6 mx-auto mb-1" />
          <div className="text-2xl font-bold">{aujourdhui.tempMax}°C</div>
          <div className="text-xs opacity-80">Max / {aujourdhui.tempMin}°C Min</div>
        </div>
        
        <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
          <Droplets className="w-6 h-6 mx-auto mb-1" />
          <div className="text-2xl font-bold">{aujourdhui.et0?.toFixed(1)}</div>
          <div className="text-xs opacity-80">ET₀ (mm/jour)</div>
        </div>
        
        <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
          <CloudRain className="w-6 h-6 mx-auto mb-1" />
          <div className="text-2xl font-bold">{aujourdhui.precipitation}</div>
          <div className="text-xs opacity-80">Pluie (mm)</div>
        </div>
        
        <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
          <Wind className="w-6 h-6 mx-auto mb-1" />
          <div className="text-2xl font-bold">{aujourdhui.windMax}</div>
          <div className="text-xs opacity-80">Vent max (km/h)</div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Prévisions 7 jours
// ═══════════════════════════════════════════════════════════════════════════

const Previsions7Jours = ({ data, stadeActuel, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-lg animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-40 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data || !data.daily) {
    return null;
  }

  const kc = stadeActuel?.kc || 1.0;

  return (
    <div className="bg-white rounded-xl p-5 shadow-lg">
      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-blue-600" />
        Prévisions 7 jours (Kc = {kc})
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b">
              <th className="text-left py-2">Date</th>
              <th className="text-center py-2">T°</th>
              <th className="text-center py-2">ET₀</th>
              <th className="text-center py-2">ETc</th>
              <th className="text-center py-2">Pluie</th>
              <th className="text-center py-2">Bilan</th>
              <th className="text-center py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.daily.map((jour, idx) => {
              const etc = jour.et0 * kc;
              const bilan = jour.precipitation - etc;
              const dateObj = new Date(jour.date);
              const jourSemaine = dateObj.toLocaleDateString('fr-FR', { weekday: 'short' });
              
              return (
                <tr key={jour.date} className={`border-b ${idx === 0 ? 'bg-blue-50' : ''}`}>
                  <td className="py-2">
                    <span className="font-medium">{jourSemaine}</span>
                    <span className="text-gray-400 ml-1">{dateObj.getDate()}/{dateObj.getMonth() + 1}</span>
                  </td>
                  <td className="text-center">
                    <span className="text-red-500">{jour.tempMax}°</span>
                    <span className="text-gray-400">/</span>
                    <span className="text-blue-500">{jour.tempMin}°</span>
                  </td>
                  <td className="text-center text-gray-600">{jour.et0?.toFixed(1)}</td>
                  <td className="text-center font-medium text-orange-600">{etc.toFixed(1)}</td>
                  <td className="text-center">
                    {jour.precipitation > 0 ? (
                      <span className="text-blue-600 flex items-center justify-center gap-1">
                        <CloudRain className="w-3 h-3" />
                        {jour.precipitation}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    <span className={`font-medium ${bilan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {bilan >= 0 ? '+' : ''}{bilan.toFixed(1)}
                    </span>
                  </td>
                  <td className="text-center">
                    {bilan < -3 ? (
                      <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs">
                        💧 Arroser
                      </span>
                    ) : bilan < 0 ? (
                      <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs">
                        ⚠️ Surveiller
                      </span>
                    ) : (
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                        ✅ OK
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Résumé 7 jours */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="bg-orange-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-orange-600">
            {data.daily.reduce((sum, j) => sum + (j.et0 * kc), 0).toFixed(0)} mm
          </div>
          <div className="text-xs text-gray-600">ETc Total 7j</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {data.daily.reduce((sum, j) => sum + j.precipitation, 0).toFixed(0)} mm
          </div>
          <div className="text-xs text-gray-600">Pluie prévue</div>
        </div>
        <div className={`rounded-lg p-3 text-center ${
          data.daily.reduce((sum, j) => sum + j.precipitation - (j.et0 * kc), 0) >= 0 
            ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className={`text-2xl font-bold ${
            data.daily.reduce((sum, j) => sum + j.precipitation - (j.et0 * kc), 0) >= 0 
              ? 'text-green-600' : 'text-red-600'
          }`}>
            {Math.abs(data.daily.reduce((sum, j) => sum + j.precipitation - (j.et0 * kc), 0)).toFixed(0)} mm
          </div>
          <div className="text-xs text-gray-600">
            {data.daily.reduce((sum, j) => sum + j.precipitation - (j.et0 * kc), 0) >= 0 
              ? '✅ Excédent' : '💧 À apporter'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT : Bilan Historique (rétrospective)
// ═══════════════════════════════════════════════════════════════════════════

const BilanHistorique = ({ bilan, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-lg animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-60 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!bilan) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl p-5 shadow-lg">
      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-purple-600" />
        📊 Bilan Hydrique - {bilan.nbJours} jours de culture
      </h3>

      {/* Résumé global */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-orange-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-orange-600">{bilan.totalETc}</div>
          <div className="text-sm text-gray-600">ETc Total (mm)</div>
          <div className="text-xs text-gray-400">Besoin culture</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{bilan.totalPluie}</div>
          <div className="text-sm text-gray-600">Pluie (mm)</div>
          <div className="text-xs text-gray-400">Apport naturel</div>
        </div>
        <div className={`rounded-lg p-4 text-center ${bilan.deficitTotal > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
          <div className={`text-3xl font-bold ${bilan.deficitTotal > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {Math.abs(bilan.deficitTotal)}
          </div>
          <div className="text-sm text-gray-600">
            {bilan.deficitTotal > 0 ? 'Déficit (mm)' : 'Excédent (mm)'}
          </div>
          <div className="text-xs text-gray-400">
            {bilan.deficitTotal > 0 ? '💧 À irriguer' : '✅ Couvert'}
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-purple-600">
            {Math.round((bilan.totalPluie / bilan.totalETc) * 100)}%
          </div>
          <div className="text-sm text-gray-600">Couverture</div>
          <div className="text-xs text-gray-400">Pluie / Besoin</div>
        </div>
      </div>

      {/* Détail par stade */}
      <h4 className="font-semibold text-gray-700 mb-3">📈 Détail par stade</h4>
      <div className="space-y-2">
        {bilan.resumeParStade.map(stade => (
          <div key={stade.code} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-20 font-medium text-gray-700">{stade.nom}</div>
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Kc = {stade.kc}</span>
                <span>{stade.nbJours} jours</span>
              </div>
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden flex">
                <div 
                  className="h-full bg-orange-400"
                  style={{ width: `${(stade.totalETc / bilan.totalETc) * 100}%` }}
                  title={`ETc: ${stade.totalETc}mm`}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>ETc: {stade.totalETc}mm</span>
                <span>Pluie: {stade.totalPluie}mm</span>
                <span className={stade.deficit > 0 ? 'text-red-500' : 'text-green-500'}>
                  {stade.deficit > 0 ? `Déficit: ${stade.deficit}mm` : `Excédent: ${Math.abs(stade.deficit)}mm`}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : BilanHydrique
// ═══════════════════════════════════════════════════════════════════════════

const BilanHydrique = ({ dateSemis, stades, stadeActuel, jourCulture }) => {
  const [forecast, setForecast] = useState(null);
  const [historique, setHistorique] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateSemis]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Charger prévisions (toujours disponibles)
      const forecastData = await meteoService.getMeteoForecast();
      setForecast(forecastData);

      // Charger historique SEULEMENT si date de semis valide et passée
      if (dateSemis) {
        const dateDebutObj = new Date(dateSemis);
        const aujourdhuiObj = new Date();
        
        // Vérifier que la date est valide et pas dans le futur
        if (!isNaN(dateDebutObj.getTime()) && dateDebutObj <= aujourdhuiObj) {
          const aujourdhui = aujourdhuiObj.toISOString().split('T')[0];
          const bilanData = await meteoService.calculerBilanHydrique(
            dateSemis,
            aujourdhui,
            stades
          );
          setHistorique(bilanData);
        } else {
          console.log('Date de semis invalide ou dans le futur:', dateSemis);
          setHistorique(null);
        }
      } else {
        setHistorique(null);
      }

    } catch (err) {
      console.error('Erreur chargement données météo:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          💧 Bilan Hydrique
          {stadeActuel && (
            <span className="text-sm font-normal bg-orange-100 text-orange-700 px-2 py-1 rounded">
              Stade {stadeActuel.nom} - Kc = {stadeActuel.kc}
            </span>
          )}
        </h2>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <AlertTriangle className="w-5 h-5 inline mr-2" />
          Erreur : {error}
        </div>
      )}

      {/* Météo du jour */}
      <MeteoJourCard data={forecast} loading={loading} />

      {/* Prévisions 7 jours */}
      <Previsions7Jours data={forecast} stadeActuel={stadeActuel} loading={loading} />

      {/* Bilan historique */}
      {historique && <BilanHistorique bilan={historique} loading={loading} />}

      {/* Message si pas d'historique */}
      {!loading && !historique && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <Calendar className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h3 className="font-semibold text-amber-800 mb-2">Pas de bilan historique disponible</h3>
          <p className="text-amber-700 text-sm">
            Pour voir le bilan hydrique d'une culture, retournez dans l'onglet 
            <strong> 🥕 Simulateur Culture</strong> et lancez une simulation avec une date de semis passée.
          </p>
          <p className="text-amber-600 text-xs mt-2">
            Exemple : Semis le 2025-06-01 → Bilan sur ~115 jours
          </p>
        </div>
      )}

      {/* Info source */}
      <div className="text-center text-sm text-gray-400">
        📡 Données Open-Meteo (gratuit) • ET₀ calculé selon FAO-56
      </div>
    </div>
  );
};

export default BilanHydrique;